import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ImageUpload from '../components/ImageUpload';
import LocationAutocomplete from '../components/LocationAutocomplete';
import { useToast } from '../hooks/use-toast';
import { Car, DollarSign, MapPin, Settings } from 'lucide-react';
import { isMobilitier } from '../lib/roles';

const AddCar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { carId } = useParams();
  const isEdit = Boolean(carId);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    price_per_day: '',
    car_type: 'sedan',
    transmission: 'automatic',
    seats: 5,
    fuel_type: 'gasoline',
    description: '',
    location: '',
    location_coords: null,
    features: []
  });

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);

  // Load car data when editing
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { carsAPI } = await import('../api');
        const res = await carsAPI.getById(carId);
        const c = res.data;
        setFormData({
          make: c.brand || '',
          model: c.model || '',
          year: c.year || new Date().getFullYear(),
          price_per_day: String(c.price_per_day || ''),
          car_type: (c.type || 'sedan').toLowerCase(),
          transmission: c.transmission || 'automatic',
          seats: c.capacity || 5,
          fuel_type: c.fuel_type || 'gasoline',
          description: c.description || '',
          location: c.location || '',
          location_coords: c.location_coords || null,
          features: c.features || [],
        });
        setSelectedFeatures(c.features || []);
        setImages(c.images || []);
      } catch (e) {
        toast({ title: 'Error', description: 'No se pudo cargar el auto', variant: 'destructive' });
        navigate('/mobilitier/dashboard');
      } finally {
        setInitialLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carId, isEdit]);

  const availableFeatures = [
    'AC', 'Bluetooth', 'GPS', 'USB', 'Cámara trasera',
    'Sensores estacionamiento', 'Asientos piel', 'Quemacocos',
    'Apple CarPlay', 'Android Auto', 'Control crucero'
  ];

  const [selectedFeatures, setSelectedFeatures] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFeatureToggle = (feature) => {
    setSelectedFeatures(prev =>
      prev.includes(feature)
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isMobilitier(user)) {
      toast({
        title: 'Error',
        description: 'Solo Mobilitiers pueden agregar autos',
        variant: 'destructive'
      });
      return;
    }

    if (!isEdit && images.length < 3) {
      toast({
        title: 'Error',
        description: 'Sube al menos 3 fotos de tu auto',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const { carsAPI } = await import('../api');
      
      const carData = {
        name: `${formData.make} ${formData.model} ${formData.year}`,
        type: formData.car_type,
        brand: formData.make,
        model: formData.model,
        year: parseInt(formData.year),
        transmission: formData.transmission,
        capacity: parseInt(formData.seats),
        price_per_day: parseFloat(formData.price_per_day),
        location: formData.location,
        location_coords: formData.location_coords,
        images: images,
        features: selectedFeatures,
        description: formData.description || `${formData.make} ${formData.model} ${formData.year}`,
      };

      if (isEdit) {
        await carsAPI.update(carId, carData);
        toast({ title: '¡Actualizado!', description: 'Los cambios fueron guardados' });
      } else {
        await carsAPI.create(carData);
        toast({ title: '¡Éxito!', description: 'Tu auto ha sido publicado' });
      }

      navigate('/mobilitier/dashboard');
    } catch (error) {
      console.error('Error creating car:', error);
      // FastAPI/Pydantic 422 returns detail as ARRAY of objects {loc, msg, type, ...}.
      // Never render objects as React children — extract msg strings.
      let description = 'Error al publicar el auto';
      const raw = error?.response?.data?.detail;
      if (Array.isArray(raw)) {
        description = raw
          .map((e) => {
            const field = Array.isArray(e?.loc) ? e.loc.slice(1).join('.') : '';
            return field ? `${field}: ${e?.msg || ''}` : (e?.msg || '');
          })
          .filter(Boolean)
          .join(' · ') || description;
      } else if (typeof raw === 'string') {
        description = raw;
      } else if (raw && typeof raw === 'object') {
        description = raw.msg || JSON.stringify(raw);
      }
      toast({
        title: 'Error',
        description,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isMobilitier(user)) {
    navigate('/');
    return null;
  }

  if (initialLoading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-gray-600">Cargando...</div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{isEdit ? 'Editar Auto' : 'Publicar mi Auto'}</h1>
          <p className="text-gray-600 mt-2">{isEdit ? 'Actualiza la información de tu auto' : 'Completa la información para empezar a ganar'}</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Información Básica */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="w-5 h-5" />
                Información del Vehículo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="make">Marca *</Label>
                  <Input
                    id="make"
                    name="make"
                    value={formData.make}
                    onChange={handleInputChange}
                    placeholder="ej. Toyota"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="model">Modelo *</Label>
                  <Input
                    id="model"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="ej. RAV4"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="year">Año *</Label>
                  <Input
                    id="year"
                    name="year"
                    type="number"
                    value={formData.year}
                    onChange={handleInputChange}
                    min="2000"
                    max={new Date().getFullYear() + 1}
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="car_type">Tipo de Auto *</Label>
                  <Select value={formData.car_type} onValueChange={(value) => setFormData(prev => ({ ...prev, car_type: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sedan">Sedán</SelectItem>
                      <SelectItem value="suv">SUV</SelectItem>
                      <SelectItem value="pickup">Pickup</SelectItem>
                      <SelectItem value="hatchback">Hatchback</SelectItem>
                      <SelectItem value="van">Van</SelectItem>
                      <SelectItem value="coupe">Coupe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="transmission">Transmisión *</Label>
                  <Select value={formData.transmission} onValueChange={(value) => setFormData(prev => ({ ...prev, transmission: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="automatic">Automática</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="seats">Capacidad (pasajeros) *</Label>
                  <Input
                    id="seats"
                    name="seats"
                    type="number"
                    value={formData.seats}
                    onChange={handleInputChange}
                    min="2"
                    max="9"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="fuel_type">Combustible *</Label>
                  <Select value={formData.fuel_type} onValueChange={(value) => setFormData(prev => ({ ...prev, fuel_type: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gasoline">Gasolina</SelectItem>
                      <SelectItem value="diesel">Diesel</SelectItem>
                      <SelectItem value="electric">Eléctrico</SelectItem>
                      <SelectItem value="hybrid">Híbrido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe las condiciones y características especiales de tu auto"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Precio y Ubicación */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Precio y Ubicación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="price_per_day">Precio por Día (MXN) *</Label>
                <Input
                  id="price_per_day"
                  name="price_per_day"
                  type="number"
                  value={formData.price_per_day}
                  onChange={handleInputChange}
                  placeholder="600"
                  min="100"
                  required
                />
                <p className="text-sm text-gray-600 mt-1">
                  Ganarás el 80% (${formData.price_per_day ? (parseFloat(formData.price_per_day) * 0.8).toFixed(0) : 0} MXN/día)
                </p>
              </div>

              <div>
                <Label htmlFor="location">Ubicación *</Label>
                <LocationAutocomplete
                  value={formData.location}
                  onChange={(val) => setFormData(prev => ({ ...prev, location: val }))}
                  onPlaceSelected={(place) => setFormData(prev => ({
                    ...prev,
                    location: place.address,
                    location_coords: { lat: place.lat, lng: place.lng }
                  }))}
                  placeholder="Ciudad de México, CDMX"
                  testId="addcar-location-input"
                />
                {formData.location_coords && (
                  <p className="text-xs text-green-600 mt-1">📍 Geolocalizado: {formData.location_coords.lat.toFixed(4)}, {formData.location_coords.lng.toFixed(4)}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Características */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Características
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {availableFeatures.map(feature => (
                  <label
                    key={feature}
                    className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition ${
                      selectedFeatures.includes(feature)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFeatures.includes(feature)}
                      onChange={() => handleFeatureToggle(feature)}
                      className="rounded"
                    />
                    <span className="text-sm">{feature}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Fotos */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Fotos del Auto *</CardTitle>
              <p className="text-sm text-gray-600">Mínimo 3 fotos, máximo 6. Incluye exterior, interior y detalles.</p>
            </CardHeader>
            <CardContent>
              <ImageUpload
                maxFiles={6}
                onUploadComplete={setImages}
                folder="cars"
                existingImages={images}
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/mobilitier/dashboard')}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#000000] hover:bg-ink-700"
              data-testid="submit-car-btn"
            >
              {loading ? (isEdit ? 'Actualizando...' : 'Publicando...') : (isEdit ? 'Guardar cambios' : 'Publicar Auto')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCar;
